import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Button, DataTable, Drawer, EmptyState } from '@/components/ui'
import { odooUrl } from '@/lib/utils/odoo-url'
import { usePoShipConfirmationLines } from '../api/queries'
import type { ShipConfirmationTracker } from '../api/types'
import { buildLineColumns } from '../utils/line-columns'
import { TERMINAL_STAGES } from '../utils/ship-confirmation-stage-meta'
import { LineExpandedPanel } from './LineExpandedPanel'

export interface PoShipmentDetailDrawerProps {
  /** Which PO's lines to show. `null` closes the drawer. */
  odooPoId: number | null
  onClose: () => void
  onConfirmLine: (tracker: ShipConfirmationTracker) => void
  onLogEstimate: (tracker: ShipConfirmationTracker) => void
  onUndoLine: (tracker: ShipConfirmationTracker) => void
  onSplitLine: (tracker: ShipConfirmationTracker) => void
  /**
   * "Confirm all open lines" — passes the PO's currently non-terminal
   * lines up so the caller can drive the existing bulk-confirm mutation
   * + `ConfirmShipDateModal` in locked-bulk mode.
   */
  onConfirmAllOpen: (openLines: ShipConfirmationTracker[]) => void
}

/**
 * "Open the PO" detail view — every line of one PO side by side, so the
 * whole shipment can be managed together ("Confirm all open lines" below)
 * or line by line (each row's own Confirm/Log estimate/Revise/Undo).
 * Mirrors the Sales Inbox pattern (one card per commercial entity,
 * line-level detail nested inside) at drawer scale — this feature's
 * detail (PO header + line list + one bulk action) doesn't need the
 * extra room a full page buys.
 *
 * Fetches with `include_terminal=true` so a resolved (received/
 * cancelled) PO still shows its full line history if reopened, even
 * though the board itself only ever surfaces active POs.
 */
export function PoShipmentDetailDrawer({
  odooPoId,
  onClose,
  onConfirmLine,
  onLogEstimate,
  onUndoLine,
  onSplitLine,
  onConfirmAllOpen,
}: PoShipmentDetailDrawerProps) {
  const { data, isLoading } = usePoShipConfirmationLines(odooPoId)
  const lines = data ?? []
  const openLines = useMemo(
    () =>
      lines.filter(
        (l) => !TERMINAL_STAGES.includes(l.stage as (typeof TERMINAL_STAGES)[number]),
      ),
    [lines],
  )
  const confirmedCount = openLines.filter((l) => l.stage === 'confirmed').length
  const first = lines[0]
  const navigate = useNavigate()

  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set())
  const toggleExpand = (id: string) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const columns: ColumnDef<ShipConfirmationTracker>[] = useMemo(
    () =>
      buildLineColumns({
        onConfirm: onConfirmLine,
        onLogEstimate,
        onUndo: onUndoLine,
        onSplit: onSplitLine,
        expandedRowIds,
        onToggleExpand: toggleExpand,
        showPoColumn: false,
        showVendorColumn: false,
      }),
    [onConfirmLine, onLogEstimate, onUndoLine, onSplitLine, expandedRowIds],
  )

  return (
    <Drawer
      open={odooPoId !== null}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      size="xl"
      title={
        first ? (
          <a
            href={odooUrl('purchase.order', first.odoo_po_id)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-text hover:underline"
          >
            {first.odoo_po_name}
          </a>
        ) : (
          'PO ship confirmation'
        )
      }
      meta={
        first ? (
          <>
            {first.vendor_name ? <span>{first.vendor_name}</span> : null}
            <span>
              {confirmedCount}/{openLines.length} open line{openLines.length === 1 ? '' : 's'}{' '}
              confirmed
            </span>
          </>
        ) : undefined
      }
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <span className="text-sm text-tertiary">
            {lines.length} line{lines.length === 1 ? '' : 's'} total
          </span>
          <span className="ml-auto" />
          <Button
            variant="secondary"
            size="sm"
            disabled={!first}
            onClick={() =>
              first &&
              void navigate({ to: '/po-ship-confirmation/$odooPoId/documents', params: { odooPoId: String(first.odoo_po_id) } })
            }
          >
            Shipping documents
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={openLines.length === 0}
            onClick={() => onConfirmAllOpen(openLines)}
          >
            Confirm all open lines
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <div className="flex h-full items-center justify-center text-secondary">Loading lines…</div>
      ) : lines.length === 0 ? (
        <EmptyState
          title="No lines found"
          description="This PO has no ship-confirmation tracker rows."
        />
      ) : (
        <DataTable
          data={lines}
          columns={columns}
          getRowId={(row) => String(row.id)}
          expandedRowIds={expandedRowIds}
          renderExpandedRow={(tracker) => <LineExpandedPanel tracker={tracker} />}
        />
      )}
    </Drawer>
  )
}
